import { Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { isObservable, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { convertFromCamelToPascal, getCurrentRouteSnapshot } from '../../helpers/utils';
import { ScEndpointFilter } from '../../models/table.model';
import { RestService } from '../../services/rest-service.service';

// @dynamic
@Injectable()
export class ScCrudService {
  public static subscription: Subject<any>;

  constructor(
    private _restService: RestService,
    private _injector: Injector,
    private _route: ActivatedRoute,
    private _store: Store,
    @Inject('env') private _env: any
  ) {}

  public get scope() {
    return {
      routeSnapshot: getCurrentRouteSnapshot(this._route),
      injector: this._injector,
      rest: this._restService,
      store: this._store,
      _unsubscribe$: ScCrudService.subscription
    };
  }

  load({ config, pageSize, page, filters, itemId, sortProp = 'Tstamp', direction = 'desc' }): Observable<any> {
    const data$ = config.filter().pipe(
      switchMap((initialFilters: any[]) => {
        if (!config.beforeLoad) {
          return of(initialFilters);
        }
        const loadResult = config.beforeLoad.call(this.scope);
        if (isObservable(loadResult)) {
          return loadResult.pipe(
            takeUntil(ScCrudService.subscription),
            map((f: ScEndpointFilter[]) => [...f, ...initialFilters])
          );
        }
        return [...loadResult, ...initialFilters];
      }),
      switchMap((initialFilters: any[]) =>
        this._restService.post(`${this._env.apiURL}/Endpoint/${config.endpoint}`, {
          filters: [...initialFilters, ...filters].filter(
            (thing, index, self) => index === self.findIndex(t => t.prop === thing.prop && t.comparison === thing.comparison)
          ),
          page,
          pageSize,
          sortModel: {
            prop: convertFromCamelToPascal(sortProp),
            direction
          }
        })
      )
    );

    return data$.pipe(
      map((data: any) => {
        const items = Array.isArray(data) ? data : data.items || [];
        return {
          ...data,
          totalCount: Array.isArray(data) ? data.length : data.totalCount,
          items: items.map(item => ({
            ...item,
            id: item[itemId] || item.id
          }))
        };
      })
    );
  }

  get({ id, config }): Observable<any> {
    const data$: Observable<any> = this._restService.get(`${this._env.apiURL}/Endpoint/${config.endpoint}/${id}`, {});
    return data$.pipe(
      map((item: any) => ({
        ...item,
        id: item[config.itemId] || item.id
      }))
    );
  }

  create({ data, config, getConfig, itemId }): Observable<any> {
    return of(data).pipe(
      switchMap(() => {
        try {
          const result = config.beforeRequest.call(this.scope, data);
          if (isObservable(result)) {
            return result;
          }
          return of(result);
        } catch (error) {
          console.log(error);
        }
        return of(data);
      }),
      switchMap(result =>
        this._restService.post(`${this._env.apiURL}/Endpoint/${config.endpoint}`, result).pipe(
          switchMap((item: any) =>
            // // NOTE: Temporary solution for endpoints that return the full entity
            // if (Object.keys(item).length > 1 || !item[itemId]) {
            //   return of(item);
            // }
            // TODO: Remove once all endpoints return entity on create.
            this.get({ id: item[itemId] || item.id, config: getConfig }).pipe(catchError(() => of(item)))
          ),
          map((item: any) => {
            if (config.afterRequest) {
              return config.afterRequest.call(this.scope, item);
            }
            return {
              ...item,
              id: item[itemId] || item.id
            };
          })
        )
      )
    );
  }

  update({ data, config, getConfig, itemId }): Observable<any> {
    return of(data).pipe(
      switchMap(() => {
        const result = config.beforeRequest.call(this.scope, data);
        if (isObservable(result)) {
          return result;
        }
        return of(result);
      }),
      switchMap(result => this._restService.post(`${this._env.apiURL}/Endpoint/${config.endpoint}`, result)),
      switchMap(item =>
        // // NOTE: Temporary solution for endpoints that return the full entity
        // if (Object.keys(item).length > 1 || !item[itemId]) {
        //   return of(item);
        // }
        this.get({ id: item[itemId] || data.id, config: getConfig }).pipe(catchError(() => of(item)))
      ),
      map((item: any) => {
        if (config.afterRequest) {
          return config.afterRequest.call(this.scope, item);
        }
        return {
          ...item,
          id: item[config.itemId] || item.id
        };
      })
    );
  }

  delete({ id, config }): Observable<any> {
    return this._restService.post(`${this._env.apiURL}/Endpoint/${config.endpoint}/${id}`, {});
  }
}
